import { useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, ChevronRight, CircleHelp, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { GuidedStepBlock } from "@/types/agentic-chat";
import type { GuidedLessonControlInput, GuidedLessonSubmitInput } from "@/types/student-toofi";
import { detectGuidedStepLocale, getGuidedPhaseLabel, getGuidedUiCopy } from "@/components/ui/reader-ai/guided-step-copy";

type GuidedStepCardProps = {
  block: GuidedStepBlock;
  onSubmit?: (input: GuidedLessonSubmitInput) => void;
  onSkip?: (input: GuidedLessonControlInput) => void;
};

export function GuidedStepCard({ block, onSubmit, onSkip }: GuidedStepCardProps) {
  const locale = useMemo(
    () => detectGuidedStepLocale(`${block.title}\n${block.content}\n${block.check.prompt}`),
    [block.check.prompt, block.content, block.title],
  );
  const copy = getGuidedUiCopy(locale);
  const isRtl = locale === "ar";
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [answerDraft, setAnswerDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const progressPercent = Math.max(4, Math.round((block.stepIndex / Math.max(1, block.totalSteps)) * 100));
  const phaseTone =
    block.phase === "summary"
      ? "border-emerald-100 bg-emerald-50 text-emerald-700"
      : block.phase === "you_do"
        ? "border-violet-100 bg-violet-50 text-violet-700"
        : block.phase === "we_do"
          ? "border-sky-100 bg-sky-50 text-sky-700"
          : "border-blue-100 bg-blue-50 text-blue-700";

  const handleSubmit = async () => {
    if (!onSubmit) return;
    if ((block.check.kind === "mcq" || block.check.kind === "true_false") && selectedIndex === null) return;
    const answer =
      block.check.kind === "mcq" || block.check.kind === "true_false"
        ? block.check.options?.[selectedIndex ?? 0]?.label ?? ""
        : answerDraft.trim();
    if (!answer) return;
    setBusy(true);
    try {
      await onSubmit({
        lessonId: block.lessonId,
        stepIndex: block.stepIndex,
        answer,
        checkKind: block.check.kind,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!onSkip) return;
    setBusy(true);
    try {
      await onSkip({
        lessonId: block.lessonId,
        stepIndex: block.stepIndex,
      });
    } finally {
      setBusy(false);
    }
  };

  const checkOptions =
    block.check.kind === "true_false"
      ? [
          { id: "true", label: copy.trueLabel },
          { id: "false", label: copy.falseLabel },
        ]
      : block.check.options ?? [];

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="overflow-hidden rounded-[1.35rem] border border-blue-100/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(239,246,255,0.78))] p-3 shadow-[0_18px_36px_rgba(59,130,246,0.10)] sm:p-3.5"
    >
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-blue-100/80">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-600 to-sky-400 transition-[width] duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge variant="outline" className={cn("rounded-full px-2.5 py-1 text-[10px] font-semibold", phaseTone)}>
          {getGuidedPhaseLabel(block.phase, locale)}
        </Badge>
        <span className="text-[11px] font-medium text-slate-500">
          {copy.step} {block.stepIndex}/{block.totalSteps}
        </span>
      </div>
      <div className="mt-2.5 space-y-2">
        <h4 className="text-[15px] font-semibold tracking-[-0.02em] text-slate-900">{block.title}</h4>
        <div className="rounded-[1rem] border border-white/90 bg-white/90 px-3 py-2.5 text-[13px] leading-7 text-slate-700">
          {block.content}
        </div>
      </div>
      <div className="mt-3 rounded-[1rem] border border-blue-100/80 bg-white/92 p-3">
        <div className="flex items-center gap-2 text-blue-700">
          <CircleHelp className="h-4 w-4" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-700">{copy.answerLabel}</p>
        </div>
        <p className="mt-2 text-[13px] font-medium leading-6 text-slate-900">{block.check.prompt}</p>
        {block.check.kind === "mcq" || block.check.kind === "true_false" ? (
          <div className="mt-3 space-y-2">
            {checkOptions.map((option, index) => {
              const active = selectedIndex === index;
              return (
                <button
                  key={option.id}
                  type="button"
                  className={cn(
                    "flex w-full items-center justify-between rounded-[0.95rem] border px-3 py-3 text-start text-[13px] font-medium transition-all",
                    active
                      ? "border-blue-300 bg-blue-50 text-blue-800 shadow-[0_12px_26px_rgba(59,130,246,0.14)] ring-1 ring-blue-100"
                      : "border-slate-200/80 bg-white text-slate-700 hover:border-blue-100 hover:bg-blue-50/60",
                  )}
                  onClick={() => setSelectedIndex(index)}
                  disabled={busy}
                >
                  <span className="min-w-0">{option.label}</span>
                  {active ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                </button>
              );
            })}
          </div>
        ) : block.check.kind === "explain_back" ? (
          <Textarea
            value={answerDraft}
            onChange={(event) => setAnswerDraft(event.target.value)}
            placeholder={block.check.prompt}
            className="mt-3 min-h-[110px] rounded-[1rem] border-blue-100/80 bg-white text-[13px] leading-6"
            dir={isRtl ? "rtl" : "auto"}
          />
        ) : (
          <Textarea
            value={answerDraft}
            onChange={(event) => setAnswerDraft(event.target.value)}
            placeholder={block.check.prompt}
            className="mt-3 min-h-[78px] rounded-[1rem] border-blue-100/80 bg-white text-[13px] leading-6"
            dir={isRtl ? "rtl" : "auto"}
          />
        )}
      </div>
      <div className="mt-3 space-y-2">
        <p className="text-[11px] leading-5 text-slate-500">{copy.helper}</p>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="text-[12px] font-medium text-slate-500 underline-offset-4 hover:text-slate-700 hover:underline"
            onClick={() => setSkipDialogOpen(true)}
            disabled={busy}
          >
            {copy.skip}
          </button>
          <Button
            type="button"
            className="h-10 rounded-full px-4 text-[12px] font-semibold shadow-[0_14px_28px_rgba(59,130,246,0.18)]"
            onClick={() => void handleSubmit()}
            disabled={
              busy ||
              ((block.check.kind === "mcq" || block.check.kind === "true_false")
                ? selectedIndex === null
                : !answerDraft.trim())
            }
          >
            {busy ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : <ArrowRight className="me-2 h-4 w-4" />}
            {copy.submit}
          </Button>
        </div>
      </div>
      <AlertDialog open={skipDialogOpen} onOpenChange={setSkipDialogOpen}>
        <AlertDialogContent className="w-[calc(100vw-1.5rem)] max-w-sm rounded-[1.5rem] border border-[var(--athr-border-soft)] bg-white/95 backdrop-blur-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{copy.skip}</AlertDialogTitle>
            <AlertDialogDescription>{copy.skipConfirm}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="rounded-full bg-rose-600 hover:bg-rose-700"
              onClick={() => {
                void handleSkip();
              }}
            >
              {copy.skip}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
